import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type ResponseType = 'treasure' | 'gift' | 'reflection' | 'invitation' | 'realignment' | null;

interface ResponseOption {
  key: ResponseType;
  keyEn: string;
  icon: string;
  color: string;
  bg: string;
  ring: string;
}

export default function LifeResponseSection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ResponseType>(null);
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState(false);

  const options: ResponseOption[] = [
    { key: 'treasure', keyEn: 'treasureEn', icon: 'ri-seedling-line', color: 'text-emerald-500', bg: 'bg-emerald-50', ring: 'ring-emerald-300' },
    { key: 'gift', keyEn: 'giftEn', icon: 'ri-gift-line', color: 'text-amber-500', bg: 'bg-amber-50', ring: 'ring-amber-300' },
    { key: 'reflection', keyEn: 'reflectionEn', icon: 'ri-pause-circle-line', color: 'text-sky-500', bg: 'bg-sky-50', ring: 'ring-sky-300' },
    { key: 'invitation', keyEn: 'invitationEn', icon: 'ri-compass-3-line', color: 'text-violet-500', bg: 'bg-violet-50', ring: 'ring-violet-300' },
    { key: 'realignment', keyEn: 'realignmentEn', icon: 'ri-loop-left-line', color: 'text-rose-500', bg: 'bg-rose-50', ring: 'ring-rose-300' },
  ];

  const handleSelect = useCallback((key: ResponseType) => {
    setSelected(key);
    setSaved(false);
  }, []);

  const handleSave = useCallback(() => {
    if (selected && note.trim()) {
      setSaved(true);
      setTimeout(() => {
        setSelected(null);
        setNote('');
        setSaved(false);
      }, 2500);
    }
  }, [selected, note]);

  const selectedOption = options.find((o) => o.key === selected);

  return (
    <section className="w-full bg-background-100/50">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2
          className="text-lg md:text-xl text-foreground-800 mb-2 text-center"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t('journey.lifeResponse.title')}
          <span className="block text-xs text-foreground-400 font-normal mt-1">
            {t('journey.lifeResponse.titleEn')}
          </span>
        </h2>

        {/* Response type selector */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mt-8">
          {options.map((opt) => {
            const isSelected = selected === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => handleSelect(opt.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? `${opt.bg} border-transparent ${opt.ring} ring-1 ${opt.color} font-medium`
                    : 'bg-background-50 border-foreground-200 text-foreground-500 hover:border-foreground-300 hover:text-foreground-700'
                }`}
              >
                <i className={`${opt.icon} text-sm`} />
                <span>{t(`journey.lifeResponse.${opt.key}`)}</span>
              </button>
            );
          })}
        </div>

        {/* Selected detail + note */}
        {selectedOption && !saved && (
          <div className="max-w-md mx-auto mt-6 bg-background-50 border border-foreground-200/60 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className={`${selectedOption.icon} ${selectedOption.color} text-lg`} />
              <span className="text-sm font-medium text-foreground-700">
                {t(`journey.lifeResponse.${selectedOption.key}`)}
              </span>
            </div>
            <p className="text-xs text-foreground-500 mb-3 leading-relaxed">
              {t(`journey.lifeResponse.${selectedOption.key}Desc`)}
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={300}
              className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
              placeholder="寫下你的感受或觀察……"
            />
            <p className="text-[11px] text-foreground-400 mt-1 mb-4 text-right">
              {note.length} / 300
            </p>
            <button
              onClick={handleSave}
              disabled={!note.trim()}
              className="w-full px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              記錄這個回應
            </button>
          </div>
        )}

        {/* Saved confirmation */}
        {saved && (
          <div className="max-w-md mx-auto mt-6 text-center bg-primary-50 border border-primary-200/40 rounded-xl p-5">
            <i className="ri-check-line text-primary-500 text-xl mb-2 inline-block" />
            <p className="text-sm text-primary-700">已記錄。這份回應會成為你今天旅程的一部分。</p>
          </div>
        )}
      </div>
    </section>
  );
}